import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, ArrowLeft, Save, X, FileText, Image as ImageIcon, AlertTriangle, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { blogService } from '../services/api';
import { BlogPost as BlogPostType } from '../types/api';
import LoadingSpinner from './ui/LoadingSpinner';
import { RelatedPostsSelector } from './ui';
import { BLOG_CATEGORIES } from '../utils/constants';

// Função helper para normalizar tags
const normalizeTags = (tags: any): string[] => {
  if (Array.isArray(tags)) {
    return tags;
  }
  if (typeof tags === 'string') {
    try {
      // Tentar fazer parse JSON primeiro
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Se não for JSON válido, tratar como string separada por vírgula
      return tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag);
    }
  }
  return [];
};

// Função helper para normalizar related_posts
const normalizeRelatedPosts = (relatedPosts: any): number[] => {
  if (Array.isArray(relatedPosts)) {
    return relatedPosts;
  }
  if (typeof relatedPosts === 'string') {
    try {
      // Tentar fazer parse JSON primeiro
      const parsed = JSON.parse(relatedPosts);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // Se não conseguir fazer parse, tratar como string simples
      return relatedPosts.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    }
  }
  return [];
};

const AdminBlog: React.FC = () => {
  const [blogData, setBlogData] = useState<BlogPostType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPostType | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'content' | 'seo'>('content');
  const [formData, setFormData] = useState<Partial<BlogPostType>>({
    title: '',
    excerpt: '',
    content: '',
    author: 'Luara Rezende',
    category: BLOG_CATEGORIES[0],
    tags: [],
    read_time: '5 min',
    featured: false,
    // SEO fields
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    // Table of contents
    table_of_contents_text: '',
    // Related posts
    related_posts: []
  });

  // Função para gerar slug a partir do título
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais
      .replace(/\s+/g, '-') // Substitui espaços por hífens
      .replace(/-+/g, '-') // Remove hífens duplicados
      .trim();
  };
  const [tagInput, setTagInput] = useState('');
  const tagInputRef = useRef<HTMLInputElement>(null);
  
  
  // Estados para upload de imagem
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Carregar dados ao montar o componente
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await blogService.getAll();
        if (response.success && response.data) {
          // Normalizar os dados, especialmente o campo tags
          const normalizedData = response.data.map((post: any) => ({
            ...post,
            tags: normalizeTags(post.tags),
            featured: Boolean(post.featured), // Converter 0/1 para boolean
            // SEO fields
            meta_description: post.meta_description || '',
            meta_keywords: post.meta_keywords || '',
            og_title: post.og_title || '',
            og_description: post.og_description || '',
            og_image: post.og_image || '',
            // Table of contents
            table_of_contents_text: post.table_of_contents_text || '',
            // Related posts
            related_posts: (() => {
              const normalized = normalizeRelatedPosts(post.related_posts);
              console.log('🔍 Normalizando related_posts:', { 
                original: post.related_posts, 
                normalized: normalized,
                postId: post.id,
                postTitle: post.title
              });
              return normalized;
            })()
          }));
          setBlogData(normalizedData);
        } else {
          console.error('Erro ao carregar Blog:', response.error || 'Erro desconhecido');
        }
      } catch (error) {
        console.error('Erro ao carregar Blog:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);


  const handleAddNew = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      author: 'Luara Rezende',
      category: BLOG_CATEGORIES[0],
      tags: [],
      read_time: '5 min',
      featured: false,
      // SEO fields
      meta_description: '',
      meta_keywords: '',
      og_title: '',
      og_description: '',
      og_image: '',
      // Table of contents
      table_of_contents_text: '',
      // Related posts
      related_posts: []
    });
    setEditingPost(null);
    setIsEditing(false);
    setShowForm(true);
    setActiveTab('content'); // Reset para aba de conteúdo
    // Reset upload states
    setImageFile(null);
    setImagePreview('');
    setUploadError('');
    setUploadProgress(0);
  };

  const handleEdit = (post: BlogPostType) => {
    // Garantir que tags seja sempre um array
    const normalizedPost = {
      ...post,
      tags: normalizeTags(post.tags),
      featured: Boolean(post.featured), // Converter 0/1 para boolean
      // SEO fields
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
      og_title: post.og_title || '',
      og_description: post.og_description || '',
      og_image: post.og_image || '',
      // Table of contents
      table_of_contents_text: post.table_of_contents_text || '',
      // Related posts
      related_posts: post.related_posts || []
    };
    
    setFormData(normalizedPost);
    setEditingPost(normalizedPost);
    setIsEditing(true);
    setShowForm(true);
    setActiveTab('content'); // Reset para aba de conteúdo
    
    // Carregar imagem existente se houver
    if (post.image && post.image !== '/images/blog/placeholder.jpg') {
      setImagePreview(post.image);
      setImageFile(null); // Não temos o arquivo original, apenas a URL
    } else {
      setImagePreview('');
      setImageFile(null);
    }
    
    setUploadError('');
    setUploadProgress(0);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este artigo?')) {
      try {
        const response = await blogService.delete(id);
        if (response.success) {
          setBlogData(prev => prev.filter(post => post.id !== id));
          alert('Artigo excluído com sucesso!');
        } else {
          alert('Erro ao excluir artigo: ' + response.error);
        }
      } catch (error) {
        console.error('Erro ao deletar Blog:', error);
        alert('Erro ao excluir artigo. Tente novamente.');
      }
    }
  };

  // Funções para gerenciar tags
  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formData.tags?.includes(trimmedTag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), trimmedTag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };


  // Funções de validação e upload de imagem
  const validateImageFile = (file: File): { isValid: boolean; error: string } => {
    // Verificar tipo MIME
    const allowedMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedMimeTypes.includes(file.type)) {
      return { isValid: false, error: 'Tipo de arquivo não permitido. Use JPG, PNG ou WebP.' };
    }

    // Verificar extensão (dupla validação)
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      return { isValid: false, error: 'Extensão de arquivo não permitida.' };
    }

    // Verificar tamanho (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'Arquivo muito grande. Máximo 5MB.' };
    }

    // Verificar se é realmente uma imagem
    if (!file.type.startsWith('image/')) {
      return { isValid: false, error: 'Arquivo não é uma imagem válida.' };
    }

    return { isValid: true, error: '' };
  };

  const handleImageSelect = (file: File) => {
    setUploadError('');
    
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      setUploadError(validation.error);
      return;
    }

    setImageFile(file);
    
    // Criar preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleImageSelect(e.target.files[0]);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let response: any;
      
      if (isEditing && editingPost) {
        // Editar artigo existente - remover campos que não devem ser enviados
        const { id, ...formDataWithoutId } = formData as any;
        const updateData = {
          ...formDataWithoutId,
          slug: formData.slug || generateSlug(formData.title || '')
        };
        
        // Se tiver arquivo, enviar com FormData
        if (imageFile) {
          setUploadProgress(0);
          try {
            response = await blogService.update(editingPost.id, updateData, imageFile);
            setUploadProgress(100);
          } catch (error) {
            setUploadError('Erro no upload da imagem');
            return;
          }
        } else {
          // Sem arquivo, enviar JSON normal
          response = await blogService.update(editingPost.id, updateData);
        }
        
        if (response.success && response.data) {
          setBlogData(prev => prev.map(post => post.id === editingPost.id ? response.data! : post));
          alert('Artigo atualizado com sucesso!');
          // Limpar estados de upload após sucesso
          setUploadProgress(0);
          setUploadError('');
        } else {
          alert('Erro ao atualizar artigo: ' + response.error);
          return;
        }
      } else {
        // Criar novo artigo
        const postData = {
          title: formData.title || '',
          slug: generateSlug(formData.title || ''),
          excerpt: formData.excerpt || '',
          content: formData.content || '',
          author: formData.author || 'Luara Rezende',
          read_time: formData.read_time || '5 min',
          category: formData.category || BLOG_CATEGORIES[0],
          tags: formData.tags || [],
          featured: formData.featured || false,
          image: formData.image || '/images/blog/placeholder.jpg',
          // SEO fields
          meta_description: formData.meta_description || '',
          meta_keywords: formData.meta_keywords || '',
          og_title: formData.og_title || '',
          og_description: formData.og_description || '',
          og_image: formData.og_image || '',
          // Table of contents
          table_of_contents_text: formData.table_of_contents_text || '',
          // Related posts
          related_posts: formData.related_posts || []
        };

        // Se tiver arquivo, enviar com FormData
        if (imageFile) {
          setUploadProgress(0);
          try {
            response = await blogService.create(postData, imageFile);
            setUploadProgress(100);
          } catch (error) {
            setUploadError('Erro no upload da imagem');
            return;
          }
        } else {
          // Sem arquivo, enviar JSON normal
          response = await blogService.create(postData);
        }

        if (response.success && response.data) {
          setBlogData(prev => [...prev, response.data!]);
          alert('Artigo criado com sucesso!');
        } else {
          alert('Erro ao criar artigo: ' + response.error);
          return;
        }
      }
      
      setShowForm(false);
      setFormData({});
      setEditingPost(null);
      setIsEditing(false);
      // Limpar estados de upload
      setImageFile(null);
      setImagePreview('');
      setUploadError('');
      setUploadProgress(0);
    } catch (error) {
      console.error('Erro ao salvar Blog:', error);
      alert('Erro ao salvar artigo. Tente novamente.');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <section className="section bg-brand-sand pt-32 min-h-screen">
        <div className="wrapper">
          <LoadingSpinner message="Carregando artigos..." />
        </div>
      </section>
    );
  }

  return (
    <section className="section bg-brand-sand pt-32 min-h-screen">
      <div className="wrapper">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="btn-secondary">
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Link>
              <div>
                <h1 className="text-3xl font-heading text-brand-slate">Gerenciar Blog</h1>
                <p className="text-brand-ink/70">Criar, editar e excluir artigos</p>
              </div>
            </div>
            <button onClick={handleAddNew} className="btn-primary">
              <Plus className="w-4 h-4" />
              Novo Artigo
            </button>
          </motion.div>

          {/* Form Modal */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-heading text-brand-slate">
                    {isEditing ? 'Editar Artigo' : 'Novo Artigo'}
                  </h2>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-brand-ink/60 hover:text-brand-ink"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-brand-slate/10 mb-6">
                  <button
                    type="button"
                    onClick={() => setActiveTab('content')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'content'
                        ? 'text-brand-accent border-b-2 border-brand-accent'
                        : 'text-brand-ink/60 hover:text-brand-slate'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    Conteúdo
                  </button>
                  {/* Aba SEO desabilitada temporariamente */}
                  {/*
                  <button
                    type="button"
                    onClick={() => setActiveTab('seo')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === 'seo'
                        ? 'text-brand-accent border-b-2 border-brand-accent'
                        : 'text-brand-ink/60 hover:text-brand-slate'
                    }`}
                  >
                    <Hash className="w-4 h-4" />
                    SEO
                  </button>
                  */}
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Aba Conteúdo */}
                  {activeTab === 'content' && (
                    <>
                      {/* Título */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Título</label>
                        <input
                          type="text"
                          value={formData.title || ''}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-lg font-medium text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                          placeholder="Digite o título do artigo"
                          required
                        />
                      </div>

                      {/* Grid de informações básicas */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <label className="text-sm font-medium text-brand-slate">Categoria</label>
                          <div className="relative">
                            <select
                              value={formData.category || ''}
                              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                              className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate bg-transparent appearance-none cursor-pointer transition-colors"
                              required
                            >
                              {BLOG_CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                              <svg className="w-4 h-4 text-brand-ink/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <label className="text-sm font-medium text-brand-slate">Tempo de Leitura</label>
                          <div className="px-4 py-3 border-b-2 border-brand-slate/20 text-brand-slate bg-brand-slate/5 rounded-t-lg">
                            5 min
                          </div>
                        </div>
                      </div>

                      {/* Resumo */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Resumo</label>
                        <textarea
                          value={formData.excerpt || ''}
                          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors resize-none bg-transparent"
                          rows={3}
                          placeholder="Breve descrição do artigo"
                          required
                        />
                      </div>

                      {/* Conteúdo */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Conteúdo</label>
                        <textarea
                          value={formData.content || ''}
                          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors resize-y min-h-[200px] bg-transparent"
                          rows={8}
                          placeholder="Conteúdo completo do artigo em Markdown"
                          required
                        />
                      </div>

                      {/* Tags */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Tags</label>
                        <div className="space-y-3">
                          {/* Tags existentes */}
                          {formData.tags && formData.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {formData.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center gap-1 bg-brand-accent/20 text-brand-slate px-3 py-1 rounded-full text-sm border border-brand-accent/30"
                                >
                                  {tag}
                                  <button
                                    type="button"
                                    onClick={() => removeTag(tag)}
                                    className="ml-1 hover:text-red-500 transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Input para nova tag */}
                          <div className="flex gap-2">
                            <input
                              ref={tagInputRef}
                              type="text"
                              value={tagInput}
                              onChange={handleTagInputChange}
                              onKeyPress={handleTagKeyPress}
                              className="flex-1 px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                              placeholder="Digite uma tag e pressione Enter"
                            />
                            <button
                              type="button"
                              onClick={addTag}
                              className="px-4 py-3 bg-brand-accent text-brand-sand rounded-lg hover:bg-brand-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!tagInput.trim()}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-xs text-brand-ink/60">
                            Digite uma tag e pressione Enter ou clique no botão + para adicionar
                          </p>
                        </div>
                      </div>

                      {/* Imagem de destaque */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Imagem de destaque</label>
                    
                    {/* Área de Upload Drag & Drop */}
                    <div
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        dragActive
                          ? 'border-brand-accent bg-brand-accent/5'
                          : 'border-brand-slate/20 hover:border-brand-accent/50'
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFileInput}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      
                      {imagePreview ? (
                        <div className="space-y-4">
                          <div className="relative inline-block">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="max-w-full max-h-48 rounded-lg shadow-sm"
                            />
                            <button
                              type="button"
                              onClick={removeImage}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="text-sm text-brand-slate">
                            <div className="flex items-center justify-center gap-2 mb-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span>Imagem selecionada: {imageFile?.name}</span>
                            </div>
                            <div className="text-xs text-brand-ink/60">
                              {(imageFile?.size || 0 / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex justify-center">
                            <div className="p-3 bg-brand-accent/10 rounded-full">
                              <ImageIcon className="w-8 h-8 text-brand-accent" />
                            </div>
                          </div>
                          <div>
                            <p className="text-brand-slate font-medium">
                              Arraste uma imagem aqui ou{' '}
                              <span className="text-brand-accent cursor-pointer">clique para selecionar</span>
                            </p>
                            <p className="text-sm text-brand-ink/60 mt-1">
                              Formatos: JPG, PNG, WebP • Máximo 5MB • Recomendado: 1200x630px
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm text-brand-ink/70 mb-1">
                          <span>Enviando imagem...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-brand-slate/10 rounded-full h-2">
                          <div
                            className="bg-brand-accent h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Error Message */}
                    {uploadError && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-2 text-red-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-sm font-medium">Erro no upload</span>
                        </div>
                        <p className="text-sm text-red-500 mt-1">{uploadError}</p>
                      </div>
                    )}

                    {/* Success Message */}
                    {uploadProgress === 100 && !uploadError && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Upload concluído!</span>
                        </div>
                      </div>
                    )}

                    {/* Help Text */}
                    <div className="mt-3 text-xs text-brand-ink/60 space-y-1">
                      <p><strong>Formatos aceitos:</strong> JPG, PNG, WebP</p>
                      <p><strong>Tamanho máximo:</strong> 5MB</p>
                      <p><strong>Dimensões recomendadas:</strong> 1200x630px (proporção 1.91:1)</p>
                      <p><strong>Segurança:</strong> Arquivos são validados e escaneados</p>
                    </div>
                  </div>

                      {/* Artigo em destaque */}
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer group">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={formData.featured || false}
                              onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 border-2 rounded transition-all duration-200 ${
                              formData.featured 
                                ? 'bg-brand-accent border-brand-accent' 
                                : 'border-brand-slate/30 group-hover:border-brand-accent/50'
                            }`}>
                              {formData.featured && (
                                <svg className="w-3 h-3 text-brand-sand m-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-medium text-brand-slate group-hover:text-brand-accent transition-colors">
                            Artigo em destaque
                          </span>
                        </label>
                      </div>

                      {/* Posts Relacionados */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Posts Relacionados</label>
                        <RelatedPostsSelector
                          selectedPosts={formData.related_posts || []}
                          onSelectionChange={(postIds) => setFormData({ ...formData, related_posts: postIds })}
                          excludePostId={editingPost?.id}
                          maxSelections={2}
                        />
                        <p className="text-xs text-brand-ink/60">
                          Selecione até 2 posts relacionados para melhorar a experiência do usuário
                        </p>
                      </div>
                    </>
                  )}

                  {/* Aba SEO - DESABILITADA TEMPORARIAMENTE */}
                  {false && activeTab === 'seo' && (
                    <>
                      {/* Meta Description */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">
                          Meta Description
                          <span className="text-xs text-brand-ink/60 ml-2">
                            ({formData.meta_description?.length || 0}/160 caracteres)
                          </span>
                        </label>
                        <textarea
                          value={formData.meta_description || ''}
                          onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors resize-none bg-transparent"
                          rows={3}
                          maxLength={160}
                          placeholder="Descrição para SEO (máximo 160 caracteres)"
                        />
                        {/* Validação de meta description comentada - aba SEO desabilitada */}
                        {/*
                        {formData.meta_description && formData.meta_description.length > 160 ? (
                          <p className="text-red-500 text-xs">
                            Meta description muito longa! Máximo 160 caracteres.
                          </p>
                        ) : null}
                        */}
                      </div>

                      {/* Meta Keywords */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Meta Keywords</label>
                        <input
                          type="text"
                          value={formData.meta_keywords || ''}
                          onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                          placeholder="palavra-chave1, palavra-chave2, palavra-chave3"
                        />
                        <p className="text-xs text-brand-ink/60">
                          Separe as palavras-chave com vírgulas
                        </p>
                      </div>

                      {/* Open Graph Title */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Open Graph Title</label>
                        <input
                          type="text"
                          value={formData.og_title || ''}
                          onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                          placeholder="Título para redes sociais"
                        />
                      </div>

                      {/* Open Graph Description */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Open Graph Description</label>
                        <textarea
                          value={formData.og_description || ''}
                          onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors resize-none bg-transparent"
                          rows={3}
                          placeholder="Descrição para redes sociais"
                        />
                      </div>

                      {/* Open Graph Image */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Open Graph Image</label>
                        <input
                          type="url"
                          value={formData.og_image || ''}
                          onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors bg-transparent"
                          placeholder="https://exemplo.com/imagem-og.jpg"
                        />
                        <p className="text-xs text-brand-ink/60">
                          URL da imagem para compartilhamento em redes sociais (recomendado: 1200x630px)
                        </p>
                      </div>

                      {/* Table of Contents Text */}
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-brand-slate">Sumário (Texto Simples)</label>
                        <textarea
                          value={formData.table_of_contents_text || ''}
                          onChange={(e) => setFormData({ ...formData, table_of_contents_text: e.target.value })}
                          className="w-full px-4 py-3 border-b-2 border-brand-slate/20 focus:border-brand-accent focus:outline-none text-brand-slate placeholder-brand-ink/40 transition-colors resize-y bg-transparent"
                          rows={4}
                          placeholder="1. Introdução&#10;2. Desenvolvimento&#10;3. Conclusão"
                        />
                        <p className="text-xs text-brand-ink/60">
                          Sumário em texto simples para exibição
                        </p>
                      </div>
                    </>
                  )}

                  <div className="flex gap-4 pt-4">
                    <button type="submit" className="btn-primary flex-1">
                      <Save className="w-4 h-4" />
                      {isEditing ? 'Salvar Alterações' : 'Criar Artigo'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}

          {/* Articles List */}
          <motion.div variants={itemVariants} className="space-y-4">
            {blogData.map((post) => (
              <div key={post.id} className="card card-pad">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="badge text-xs">{post.category}</span>
                      {post.featured && (
                        <span className="badge bg-brand-accent text-brand-sand text-xs">
                          Destaque
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-heading text-brand-slate mb-2">
                      {post.title}
                    </h3>
                    <p className="text-brand-ink/80 text-sm mb-3 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-brand-ink/60">
                      <span>{post.author}</span>
                      <span>{new Date(post.date).toLocaleDateString('pt-BR')}</span>
                      <span>{post.read_time}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(post)}
                      className="p-2 text-brand-slate hover:text-brand-accent transition-colors"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="p-2 text-red-500 hover:text-red-700 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AdminBlog;
